import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, AlertCircle } from 'lucide-react';
import {
  api,
  type Server,
  type ServerMetrics,
  type ServerDiskEntry,
  type ServerProcessEntry,
  type ServerChromeStatus,
} from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn, formatBytes, formatUptime, relativeTime } from '@/lib/utils';

export function ServerMetricsPanel({ server }: { server: Server }) {
  const qc = useQueryClient();
  const refresh = useMutation({
    mutationFn: () => api.post(`/api/servers/${server.id}/metrics-refresh`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['server', server.id] }),
  });

  const metrics = server.metrics_detail;
  const hasData = metrics && (metrics.load || metrics.memory || metrics.disks.length > 0);

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle>System metrics</CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            {server.last_metrics_at
              ? `Updated ${relativeTime(server.last_metrics_at)}`
              : 'No data yet'}
            {metrics?.os && <span className="ml-2 text-slate-400">· {metrics.os}</span>}
            {metrics?.kernel && <span className="ml-2 text-slate-400 font-mono">{metrics.kernel}</span>}
            {metrics?.uptime_seconds != null && (
              <span className="ml-2 text-slate-400">· up {formatUptime(metrics.uptime_seconds)}</span>
            )}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
          className="self-start sm:self-auto shrink-0"
        >
          <RefreshCw size={12} className={refresh.isPending ? 'animate-spin' : ''} />
          {refresh.isPending ? 'Refreshing…' : 'Refresh'}
        </Button>
      </CardHeader>
      <CardBody className="space-y-5">
        {metrics?.error && (
          <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 rounded-md px-3 py-2">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-medium">Collection failed</div>
              <div className="text-red-600/80 mt-0.5 font-mono break-all">{metrics.error}</div>
            </div>
          </div>
        )}

        {!hasData && !metrics?.error && (
          <p className="text-sm text-slate-500">
            Metrics will appear after the next background poll, or click Refresh to fetch now.
          </p>
        )}

        {metrics?.load && (
          <LoadAverage load={metrics.load} cpuCount={metrics.cpu_count} />
        )}

        {metrics?.memory && <MemoryBar memory={metrics.memory} swap={metrics.swap} />}

        {metrics && metrics.disks.length > 0 && <DiskList disks={metrics.disks} />}

        {metrics?.chrome?.present && <BrowserStatus chrome={metrics.chrome} />}

        {metrics && (metrics.top_cpu.length > 0 || metrics.top_mem.length > 0) && (
          <TopProcesses topCpu={metrics.top_cpu} topMem={metrics.top_mem} />
        )}
      </CardBody>
    </Card>
  );
}

function LoadAverage({
  load,
  cpuCount,
}: {
  load: NonNullable<ServerMetrics['load']>;
  cpuCount: number | null;
}) {
  const cores = cpuCount ?? 1;
  return (
    <div>
      <SectionLabel>Load average</SectionLabel>
      <div className="flex items-baseline gap-6">
        {(['1m', '5m', '15m'] as const).map((key) => {
          const v = load[key];
          const pct = (v / cores) * 100;
          const tone = pct >= 100 ? 'text-red-600' : pct >= 70 ? 'text-amber-600' : 'text-slate-900';
          return (
            <div key={key}>
              <div className={cn('text-2xl font-semibold tabular-nums', tone)}>{v.toFixed(2)}</div>
              <div className="text-xs text-slate-500 uppercase tracking-wide mt-0.5">{key}</div>
            </div>
          );
        })}
        {cpuCount != null && (
          <div className="ml-auto text-xs text-slate-500">
            {cpuCount} CPU{cpuCount === 1 ? '' : 's'}
          </div>
        )}
      </div>
    </div>
  );
}

function MemoryBar({
  memory,
  swap,
}: {
  memory: NonNullable<ServerMetrics['memory']>;
  swap: ServerMetrics['swap'];
}) {
  const pct = memory.total_kb > 0 ? (memory.used_kb / memory.total_kb) * 100 : 0;
  return (
    <div>
      <SectionLabel>Memory</SectionLabel>
      <UsageBar
        percent={pct}
        label={`${formatBytes(memory.used_kb * 1024)} of ${formatBytes(memory.total_kb * 1024)}`}
        sublabel={`${pct.toFixed(1)}% used · ${formatBytes(memory.available_kb * 1024)} available`}
      />
      <div className="mt-2 text-[11px] text-slate-500 flex flex-wrap gap-x-4">
        <span>Free: {formatBytes(memory.free_kb * 1024)}</span>
        <span>Cached: {formatBytes(memory.cached_kb * 1024)}</span>
        <span>Buffers: {formatBytes(memory.buffers_kb * 1024)}</span>
      </div>
      {swap && swap.total_kb > 0 && (
        <div className="mt-3">
          <SectionLabel>Swap</SectionLabel>
          <UsageBar
            percent={(swap.used_kb / swap.total_kb) * 100}
            label={`${formatBytes(swap.used_kb * 1024)} of ${formatBytes(swap.total_kb * 1024)}`}
            sublabel={`${formatBytes(swap.free_kb * 1024)} free`}
          />
        </div>
      )}
    </div>
  );
}

function DiskList({ disks }: { disks: ServerDiskEntry[] }) {
  return (
    <div>
      <SectionLabel>Disks</SectionLabel>
      <div className="space-y-2">
        {disks.map((d) => (
          <div key={`${d.filesystem}|${d.mount}`}>
            <div className="flex items-baseline justify-between gap-3 text-xs text-slate-700">
              <span className="font-mono truncate">{d.mount}</span>
              <span className="text-slate-500 font-mono shrink-0">{d.filesystem}</span>
            </div>
            <UsageBar
              percent={d.use_percent}
              label={`${formatBytes(d.used_bytes)} of ${formatBytes(d.total_bytes)}`}
              sublabel={`${d.use_percent.toFixed(0)}% used · ${formatBytes(d.available_bytes)} free`}
              compact
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// The shared browser sidecar that monitor-mode chats drive. Hidden entirely on
// hosts that don't run one — that's a single-server install, not a fault, and
// the matching alert ignores it for the same reason.
function BrowserStatus({ chrome }: { chrome: ServerChromeStatus }) {
  const state = !chrome.docker_ok
    ? { tone: 'muted' as const, text: 'Unknown — docker did not answer' }
    : !chrome.running
      ? { tone: 'bad' as const, text: 'Container stopped' }
      : chrome.cdp !== 'ok'
        ? { tone: 'bad' as const, text: 'Running, but not answering' }
        : {
            tone: 'ok' as const,
            text: `Running · ${chrome.targets ?? '?'} tab${chrome.targets === 1 ? '' : 's'} open`,
          };

  return (
    <div>
      <SectionLabel>Browser tools</SectionLabel>
      <div
        className={cn(
          'flex items-center gap-2 rounded-md border px-3 py-2 text-xs',
          state.tone === 'ok' && 'border-slate-200 text-slate-700',
          state.tone === 'bad' && 'border-red-200 bg-red-50 text-red-700',
          state.tone === 'muted' && 'border-slate-200 bg-slate-50 text-slate-500'
        )}
      >
        <span
          className={cn(
            'h-1.5 w-1.5 rounded-full shrink-0',
            state.tone === 'ok' && 'bg-emerald-500',
            state.tone === 'bad' && 'bg-red-500',
            state.tone === 'muted' && 'bg-slate-400'
          )}
        />
        <span>{state.text}</span>
      </div>
    </div>
  );
}

function TopProcesses({
  topCpu,
  topMem,
}: {
  topCpu: ServerProcessEntry[];
  topMem: ServerProcessEntry[];
}) {
  return (
    <div>
      <SectionLabel>Top processes</SectionLabel>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {topCpu.length > 0 && <ProcTable title="By CPU" rows={topCpu} sortBy="cpu" />}
        {topMem.length > 0 && <ProcTable title="By memory" rows={topMem} sortBy="mem" />}
      </div>
    </div>
  );
}

function ProcTable({
  title,
  rows,
  sortBy,
}: {
  title: string;
  rows: ServerProcessEntry[];
  sortBy: 'cpu' | 'mem';
}) {
  return (
    <div className="rounded-md border border-slate-200 overflow-hidden">
      <div className="px-3 py-1.5 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
        {title}
      </div>
      <div className="overflow-x-auto">
      <table className="w-full min-w-[360px] text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
            <th className="px-3 py-1.5 font-medium">PID</th>
            <th className="px-2 py-1.5 font-medium">User</th>
            <th className="px-2 py-1.5 font-medium">Command</th>
            <th className="px-2 py-1.5 font-medium text-right">CPU</th>
            <th className="px-3 py-1.5 font-medium text-right">RSS</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p, i) => (
            <tr key={`${p.pid}-${i}`} className="border-b border-slate-100 last:border-0">
              <td className="px-3 py-1.5 font-mono text-slate-500">{p.pid}</td>
              <td className="px-2 py-1.5 text-slate-700 truncate max-w-[120px]">{p.user}</td>
              <td className="px-2 py-1.5 text-slate-900 font-mono truncate max-w-[200px]">{p.command}</td>
              <td
                className={cn(
                  'px-2 py-1.5 text-right tabular-nums',
                  sortBy === 'cpu' ? 'font-semibold text-slate-900' : 'text-slate-700'
                )}
              >
                {p.cpu_percent.toFixed(1)}%
              </td>
              <td
                className={cn(
                  'px-3 py-1.5 text-right tabular-nums',
                  sortBy === 'mem' ? 'font-semibold text-slate-900' : 'text-slate-700'
                )}
              >
                {formatBytes(p.rss_kb * 1024)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function UsageBar({
  percent,
  label,
  sublabel,
  compact,
}: {
  percent: number;
  label: string;
  sublabel?: string;
  compact?: boolean;
}) {
  const clamped = Math.max(0, Math.min(100, percent));
  const tone =
    clamped >= 90 ? 'bg-red-500' : clamped >= 75 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div>
      <div
        className={cn(
          'flex items-baseline justify-between gap-3 text-xs',
          compact ? 'mt-0.5' : ''
        )}
      >
        <span className="text-slate-900 font-medium">{label}</span>
        {sublabel && <span className="text-slate-500">{sublabel}</span>}
      </div>
      <div
        className={cn(
          'mt-1 w-full rounded-full overflow-hidden bg-slate-100',
          compact ? 'h-1.5' : 'h-2'
        )}
      >
        <div className={cn('h-full transition-all', tone)} style={{ width: `${clamped}%` }} />
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-2">
      {children}
    </div>
  );
}
